import styled from '@emotion/styled'
import { cardBorderColor, MobileCardFrame } from '../../../../components/advice/shared'
import { MOBILE_VIEWPORT } from '../../../../guided-toolbox/utils/breakpoints'
import {
  VALUE_COLUMN_WIDTH,
  COLUMN_GAP,
  ROW_TO_SENTENCE_GAP,
  nameGutterStyles,
} from '../../elements'

export { HeaderRow, ValueRow, RowGroups, ValueColumnHeading, BalanceGutter } from '../../elements'

export const Heading = styled.h1`
  font-family: 'Open Sans', 'Helvetica Neue', sans-serif;
  font-size: 1.875rem;
  font-weight: 400;
  line-height: 1.3;
  text-align: center;
  max-width: 720px;
  margin: 0 0 48px;
  color: ${p => p.theme.mediumGray};

  strong {
    color: ${p => p.theme.darkestGray};
  }

  @media ${MOBILE_VIEWPORT} {
    font-size: 1.5rem;
  }
`

const VALUE_BLOCK_WIDTH = `calc(${VALUE_COLUMN_WIDTH} * 2 + ${COLUMN_GAP})`

const AMOUNT_TO_MATCH_GAP = '10px'
const ADDITIONAL_SAVINGS_ROW_GAP = '10px'

// Only the two value columns (CURRENT / RECOMMENDED-YOUR CHANGES) are centered on
// the page. Every row is a full-width flex line with an equal-flex gutter on
// each side of the value columns; the plan name renders into the left gutter
// (see PlanName/IraName/MatchLabel) and hangs to the left, while the empty right
// gutter (BalanceGutter) balances it so the value block stays centered. Because the
// name is in normal flow, a wrapped multi-line name grows the row height rather
// than overflowing, so it can never overlap the next row. This matches the
// CompanyStock step.
export const DesktopTable = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`

// Reserve the CURRENT column when a row only has a recommended value.
export const Spacer = styled.div`
  width: ${VALUE_COLUMN_WIDTH};
`

export const AdditionalSavings = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: ${ADDITIONAL_SAVINGS_ROW_GAP};
`

export const PlanSection = styled.div`
  display: flex;
  flex-direction: column;
`

export const PlanName = styled.div`
  ${nameGutterStyles}
  font-size: 13px;
  font-weight: 600;
  color: ${p => p.theme.darkestGray};
`

export const IraName = styled.div`
  ${nameGutterStyles}
  font-size: 13px;
  font-weight: 400;
  color: ${p => p.theme.mediumGray};
`

export const AmountCell = styled.div`
  width: ${VALUE_COLUMN_WIDTH};
  box-sizing: border-box;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  font-weight: ${p => (p.planned ? 700 : 400)};
  background: ${p => (p.planned ? p.theme.ternaryColor : p.theme.addButtonHoverColor)};
`

export const AmountNumber = styled.span`
  color: ${p => p.theme.darkestGray};
`

export const AmountUnit = styled.span`
  font-weight: 400;
  color: ${p => (p.planned ? p.theme.primaryColor : p.theme.lightestGray)};
`

export const NoChange = styled.span`
  font-weight: 400;
  color: ${p => p.theme.primaryColor};
`

export const MatchRow = styled.div`
  margin-top: ${AMOUNT_TO_MATCH_GAP};
  display: flex;
  column-gap: ${COLUMN_GAP};
  align-items: center;
`

export const MatchLabel = styled.div`
  ${nameGutterStyles}
  font-size: 13px;
  color: ${p => p.theme.mediumGray};
`

export const MatchValue = styled.div`
  width: ${VALUE_COLUMN_WIDTH};
  box-sizing: border-box;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  color: ${p => p.theme.darkestGray};
  background: ${p => (p.planned ? p.theme.ternaryColor : p.theme.addButtonHoverColor)};
`

export const TaxSentenceRow = styled.div`
  margin-top: ${ROW_TO_SENTENCE_GAP};
  display: flex;
  column-gap: ${COLUMN_GAP};
`

// Bounded to the width of the two value columns and centered under them (via the
// flanking BalanceGutters), matching the pre-refactor grid-column: 2 / 4 span.
export const TaxSentence = styled.div`
  width: ${VALUE_BLOCK_WIDTH};
  text-align: center;
  font-size: 13px;
  font-weight: 400;
  color: ${p => p.theme.darkestGray};
`

export const MobileCards = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  row-gap: 30px;
`

export const MobileCard = styled(MobileCardFrame)`
  width: 100%;
  max-width: 400px;
  padding: 24px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
`

export const MobilePlanName = styled.div`
  text-align: center;
  font-size: 13px;
  font-weight: 600;
  color: ${p => p.theme.darkestGray};
`

export const MobileTaxSentence = styled.div`
  margin-top: 10px;
  text-align: center;
  font-size: 13px;
  color: ${p => p.theme.darkestGray};
`

export const MobileValueRow = styled.div`
  margin-top: 24px;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  column-gap: 20px;
`

export const MobileAmountColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  row-gap: 2px;
  min-width: 72px;
`

export const MobileAmountValue = styled.div`
  font-size: 15px;
  font-weight: ${p => (p.planned ? 600 : 400)};
  color: ${p => (p.planned ? p.theme.primaryColor : p.theme.mediumGray)};
`

export const MobileNoChange = styled.div`
  font-size: 15px;
  font-weight: 400;
  color: ${p => p.theme.primaryColor};
`

// Keep the arrow aligned with the first line of the amount.
export const MobileArrow = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 17px;
  color: ${p => p.theme.primaryColor};
  visibility: ${p => (p.invisible ? 'hidden' : 'visible')};
`

export const MobileMatch = styled.div`
  margin-top: 24px;
  text-align: center;
  font-size: 14px;
  color: ${p => p.theme.primaryColor};
`

export const MobileDivider = styled.hr`
  width: 100%;
  border: none;
  border-top: 2px solid ${cardBorderColor};
  margin: 24px 0;
`
